import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import Stack from '@mui/material/Stack/Stack';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import Details from '@/shared/components/panels/CostsDetails/Details';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import { AppPath } from '@/shared/constants/path';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';

import useCart from '../components/Cart/hooks/useCart';
import useTile from '../components/Tile/hooks/useTile';
import CancelOrderDialog from './components/CancelOrderDialog';
import OrderCancelledWarningDialog from './components/OrderCancelledWarningDialog';
import PaymentDialog from './components/PaymentDialog/PaymentDialog';
import PaymentsList from './components/PaymentsList';
import Receipt from './components/Receipt';
import TilesPanel from './components/TilesPanel';
import Title from './components/Title';
import useCartInitialization from './hooks/useCartInitialization';
import useCheckoutPayments from './hooks/useCheckoutPayments';
import useOrder from './hooks/useOrder';
import useRequiredTiles from './hooks/useRequiredTiles';

const CheckoutPayments: FC = () => {
  const navigate = useNavigate();

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const cartState = useCart();

  const orderDetailsState = useOrder({
    dialogProps: {
      onUpdateDialogState,
      onUpdateProgressDialog,
      onUpdateNotificationBar,
    },
  });

  useCartInitialization(
    cartState,
    orderDetailsState.order,
    orderDetailsState.orderQuote,
  );

  const tileState = useTile({
    listName: 'checkout-payments',
    updateDialogStates: {
      onUpdateDialogState,
      onUpdateProgressDialog,
      onUpdateNotificationBar,
    },
  });

  useRequiredTiles({ tileState });

  const { handleCreatePayment } = useCheckoutPayments({
    dialogProps: {
      onUpdateDialogState,
      onUpdateProgressDialog,
      onUpdateNotificationBar,
    },
    orderDetailsState,
  });

  const handleFinalize = () => {
    cartState.clearCart();
    cartState.onUnassignCustomer();
    cartState.onUnassignQuote();
    navigate(AppPath.Checkout);
  };

  const { order, amountOwed, totalCostInfo, handleCloseOrder } =
    orderDetailsState;

  return (
    <div>
      <Title
        assignedCustomer={cartState?.assignedCustomer}
        onFinalize={handleFinalize}
        onCancelOrder={() => {
          onUpdateDialogState(DialogName.CancelOrder, {
            open: true,
            errors: [],
          });
        }}
      />

      <Stack
        sx={{
          gap: 2,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr  minmax(500px, 1fr)',
          alignItems: 'flex-start',
        }}
      >
        <Stack spacing={2}>
          <TilesPanel tileState={tileState} />
          {order && <PaymentsList order={order} amountOwed={amountOwed} />}
        </Stack>

        <Stack spacing={2}>
          <Receipt
            products={cartState.productsInCart}
            currency={order?.currency}
          />
          <Details
            shippingPrice={totalCostInfo.shipping_price}
            orderTotal={totalCostInfo.order_total}
            subTotal={totalCostInfo.sub_total}
            currency={order?.currency}
            discount={totalCostInfo.discount}
            tax={totalCostInfo.tax}
          />
        </Stack>
      </Stack>

      {dialog?.cashPayment?.open && (
        <PaymentDialog
          variant={DialogName.CashPayment}
          errors={dialog?.cashPayment?.errors}
          amountOwed={amountOwed}
          onClose={() => {
            onUpdateDialogState(DialogName.CashPayment, {
              open: false,
              errors: [],
            });
          }}
          onCreatePayment={(amountReceived) => {
            void handleCreatePayment(amountReceived, 'CASH');
          }}
        />
      )}

      {dialog?.externalTerminalPayment?.open && (
        <PaymentDialog
          variant={DialogName.ExternalTerminalPayment}
          errors={dialog?.externalTerminalPayment?.errors}
          amountOwed={amountOwed}
          onClose={() => {
            onUpdateDialogState(DialogName.ExternalTerminalPayment, {
              open: false,
              errors: [],
            });
          }}
          onCreatePayment={(amountReceived) => {
            void handleCreatePayment(amountReceived, 'EXTERNAL');
          }}
        />
      )}

      {dialog?.cancelOrder?.open && (
        <CancelOrderDialog
          order={order}
          errors={dialog?.cancelOrder?.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.CancelOrder, {
              open: false,
              errors: [],
            });
          }}
          proceed={(isCloseWithRefund) => {
            void handleCloseOrder(isCloseWithRefund);
          }}
        />
      )}

      {order && order.order_status === 'CANCELLED' && (
        <OrderCancelledWarningDialog
          orderId={order.in_order_id}
          onCreateNewOrderClick={handleFinalize}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default CheckoutPayments;
